export type Bi = { fr: string; en: string }
export type Line = { text: Bi; accent?: boolean }

export const LOGO_ATNA = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png'

/* Generated, clearly illustrative images — not archival photos. */
export const storyImages = {
  queue: 'https://cdn.magicpatterns.com/patterns/generated-images/120fee2b-362c-48cc-9da2-73965ede9e5e.jpg',
  rebuild: 'https://cdn.magicpatterns.com/patterns/generated-images/cb57fd40-fa58-4e01-8636-40f17cadfe5a.jpg',
  listening: 'https://cdn.magicpatterns.com/patterns/generated-images/e43d3941-9166-44fb-a665-c8368b59b32d.jpg',
  team: 'https://cdn.magicpatterns.com/patterns/generated-images/8f450772-8744-476a-b00d-e5f29678761f.jpg',
  horizon: 'https://cdn.magicpatterns.com/patterns/generated-images/a230c385-7edc-4452-979e-709e9269e196.jpg',
}

export const illustrativeLabel: Bi = { fr: 'Image illustrative', en: 'Illustrative image' }

/* ───────── Act 1 · The problem that started everything ───────── */
export const act1 = {
  eyebrow: { fr: 'Notre histoire', en: 'Our story' } as Bi,
  headline: [
    { text: { fr: 'Tout a commencé', en: 'It all started' } },
    { text: { fr: 'par une file d’attente.', en: 'with a lineup.' }, accent: true },
  ] as Line[],
  body: [
    {
      fr: 'À Brossard, jusqu’à 400 personnes pouvaient se présenter pour renouveler leur carte citoyenne. Certaines attendaient jusqu’à 3 h 45.',
      en: 'In Brossard, up to 400 people could show up to renew their citizen card. Some waited up to 3 hours and 45 minutes.',
    },
    {
      fr: 'Nous avons mis notre expérience en identité numérique au service de la carte citoyenne.',
      en: 'We took experience we had gained in digital identity and applied it to the citizen card.',
    },
  ] as Bi[],
  imageAlt: { fr: 'Une longue file d’attente dans un hall de services municipaux.', en: 'A long lineup in a municipal service hall.' } as Bi,
  before: { value: '400', label: { fr: 'personnes', en: 'people' } as Bi, sub: { fr: '3 h 45 d’attente', en: '3 h 45 min wait' } as Bi },
  after: { value: '5', label: { fr: 'personnes', en: 'people' } as Bi, sub: { fr: 'quelques minutes d’attente', en: 'a few minutes’ wait' } as Bi },
  afterNote: { fr: 'Six mois après le lancement.', en: 'Six months after launch.' } as Bi,
  transition: [
    { text: { fr: 'Nous avions réglé', en: 'We had solved' } },
    { text: { fr: 'un problème de file d’attente.', en: 'a lineup problem.' } },
    { text: { fr: 'Mais nous venions surtout de découvrir', en: 'But above all, we had just discovered' }, accent: true },
    { text: { fr: 'un problème beaucoup plus grand.', en: 'a much bigger problem.' }, accent: true },
  ] as Line[],
}

/* ───────── Act 2 · The bigger vision — and the hard part ───────── */
export const act2 = {
  headline: [
    { text: { fr: 'Nous avons voulu connecter', en: 'We set out to connect' } },
    { text: { fr: 'ce qui ne l’était pas.', en: 'what wasn’t connected.' }, accent: true },
  ] as Line[],
  body: [
    {
      fr: 'L’identité n’était que le début. Les services, les communications, les données et les systèmes étaient dispersés dans des silos.',
      en: 'Identity was only the beginning. Services, communications, data and systems were scattered across silos.',
    },
    {
      fr: 'Nous avons commencé à construire une plateforme capable de les faire travailler ensemble.',
      en: 'We started building a platform that could make them work together.',
    },
  ] as Bi[],
  nodes: [
    { id: 'identity', label: { fr: 'Identité', en: 'Identity' } },
    { id: 'communications', label: { fr: 'Communications', en: 'Communications' } },
    { id: 'data', label: { fr: 'Données', en: 'Data' } },
    { id: 'systems', label: { fr: 'Systèmes', en: 'Systems' } },
    { id: 'services', label: { fr: 'Services', en: 'Services' } },
  ] as { id: string; label: Bi }[],
  foundationLabel: { fr: 'Fondation commune', en: 'Shared foundation' } as Bi,
  early: [
    { text: { fr: 'Nous étions en avance.', en: 'We were early.' } },
    { text: { fr: 'Parfois trop.', en: 'Sometimes too early.' }, accent: true },
  ] as Line[],
  stumble: [
    { fr: 'Nous avons essayé. Nous nous sommes parfois trompés.', en: 'We tried. Sometimes we got it wrong.' },
    {
      fr: 'Nous avons exploré des chemins qui n’étaient pas les nôtres. Nous avons dû reconstruire notre technologie. Une migration entre deux générations de plateforme a presque eu raison de l’entreprise.',
      en: 'We explored paths that weren’t ours. We had to rebuild our technology. A migration between two generations of the platform nearly got the better of the company.',
    },
  ] as Bi[],
  imageAlt: { fr: 'Une équipe reconstruit une architecture sur un tableau blanc, tard le soir.', en: 'A team rebuilding an architecture on a whiteboard late in the evening.' } as Bi,
  pivot: [
    { fr: 'Nous avons fait des erreurs.', en: 'We made mistakes.' },
    { fr: 'Mais nous n’avons jamais abandonné le problème.', en: 'But we never gave up on the problem.' },
  ] as Bi[],
  lessonIntro: {
    fr: 'C’est là que nous avons appris une leçon qui nous guide encore aujourd’hui :',
    en: 'That’s where we learned a lesson that still guides us today:',
  } as Bi,
  lesson: [
    { text: { fr: 'La technologie doit', en: 'Technology must' } },
    { text: { fr: 'enlever de la complexité,', en: 'remove complexity,' } },
    { text: { fr: 'pas en ajouter.', en: 'not add to it.' }, accent: true },
  ] as Line[],
}

/* ───────── Act 3 · What we learned ───────── */
export const act3 = {
  headline: [
    { text: { fr: 'Nous avons appris', en: 'We learned' } },
    { text: { fr: 'à mieux écouter.', en: 'to listen better.' }, accent: true },
  ] as Line[],
  body: {
    fr: 'Nos clients vivent leurs réalités chaque jour. Ils voient des choses que la technologie, à elle seule, ne peut pas voir.',
    en: 'Our clients live their realities every day. They see things that technology alone cannot see.',
  } as Bi,
  role: { fr: 'Notre rôle n’est pas d’arriver avec toutes les réponses.', en: 'Our role isn’t to arrive with all the answers.' } as Bi,
  roleSteps: [
    { fr: 'C’est d’écouter.', en: 'It’s to listen.' },
    { fr: 'De comprendre.', en: 'To understand.' },
    { fr: 'De poser les bonnes questions.', en: 'To ask the right questions.' },
    { fr: 'Et de construire avec eux.', en: 'And to build with them.' },
  ] as Bi[],
  imageAlt: { fr: 'Des mains déplacent des notes sur une carte de processus pendant une séance de travail.', en: 'Hands moving notes on a process map during a working session.' } as Bi,
  statement: [
    { text: { fr: 'Nos clients connaissent', en: 'Our clients know' } },
    { text: { fr: 'leurs problèmes', en: 'their problems' } },
    { text: { fr: 'mieux que personne.', en: 'better than anyone.' }, accent: true },
  ] as Line[],
  giftHeadline: [
    { text: { fr: 'Chaque problème', en: 'Every problem' } },
    { text: { fr: 'est un cadeau.', en: 'is a gift.' }, accent: true },
  ] as Line[],
  giftBody: {
    fr: 'Un problème mérite qu’on s’y attarde, qu’on le comprenne vraiment et qu’on mobilise plusieurs cerveaux autour de lui.',
    en: 'A problem deserves time, real understanding and several minds gathered around it.',
  } as Bi,
  giftLines: [
    { text: { fr: 'Si on s’attarde,', en: 'If we take the time,' } },
    { text: { fr: 'si on creuse,', en: 'if we dig deeper,' } },
    { text: { fr: 'si on rassemble les cerveaux,', en: 'if we bring minds together,' } },
    { text: { fr: 'on trouve', en: 'we find' } },
    { text: { fr: 'des solutions inédites.', en: 'solutions no one had found before.' }, accent: true },
  ] as Line[],
  attribution: { fr: 'Inspiré notamment par la pensée de David Deutsch.', en: 'Inspired in part by the thinking of David Deutsch.' } as Bi,
}

/* ───────── Act 4 · Technology catches up ───────── */
export const act4 = {
  headline: [
    { text: { fr: 'Puis la technologie', en: 'Then technology' } },
    { text: { fr: 'a rattrapé la vision.', en: 'caught up with the vision.' }, accent: true },
  ] as Line[],
  llm: {
    label: { fr: 'Les grands modèles de langage', en: 'LLMs' } as Bi,
    text: { fr: 'Comprennent enfin le langage et l’intention.', en: 'Finally understand language and intent.' } as Bi,
  },
  foundation: {
    label: { fr: 'Notre fondation', en: 'Our foundation' } as Bi,
    lead: { fr: 'Comprend déjà :', en: 'Already understands:' } as Bi,
    items: [
      { fr: 'Identité', en: 'Identity' },
      { fr: 'Contexte', en: 'Context' },
      { fr: 'Règles', en: 'Rules' },
      { fr: 'Systèmes', en: 'Systems' },
      { fr: 'Services', en: 'Services' },
    ] as Bi[],
  },
  born: [
    { text: { fr: 'ATNA est née', en: 'ATNA was born' } },
    { text: { fr: 'de cette rencontre.', en: 'from that convergence.' }, accent: true },
  ] as Line[],
  notJust: { fr: 'Pas simplement pour répondre.', en: 'Not simply to answer.' } as Bi,
  verbs: [
    { fr: 'Comprendre', en: 'Understand' },
    { fr: 'Vérifier', en: 'Verify' },
    { fr: 'Recommander', en: 'Recommend' },
    { fr: 'Orchestrer', en: 'Orchestrate' },
    { fr: 'Agir', en: 'Act' },
  ] as Bi[],
}

/* ───────── Act 5 · Who we are today ───────── */
export const act5 = {
  headline: [
    { text: { fr: 'Une petite équipe.', en: 'A small team.' } },
    { text: { fr: 'Une grande ambition.', en: 'A big ambition.' }, accent: true },
  ] as Line[],
  imageAlt: { fr: 'Une équipe travaille devant un mur de petits carrés colorés.', en: 'A team working in front of a wall of small coloured squares.' } as Bi,
  place: {
    fr: 'Nous travaillons un peu comme r/place sur Reddit. Chacun possède son territoire, en prend soin et cherche constamment à l’améliorer. La force apparaît lorsque tous les territoires se connectent pour construire quelque chose de beaucoup plus grand.',
    en: 'We work a bit like r/place on Reddit. Everyone owns their territory, takes care of it and constantly looks to improve it. Our strength emerges when all those territories connect to build something much bigger.',
  } as Bi,
  partners: {
    fr: 'Et nous ne construisons pas seuls. Nous sommes entourés d’experts chevronnés issus de différentes universités et de partenaires financiers qui continuent de croire en nous et de nous appuyer. Leur expertise, leur confiance et leur regard nous aident à aller plus loin.',
    en: 'And we don’t build alone. We are surrounded by seasoned experts from different universities and by financial partners who continue to believe in us and support us. Their expertise, trust and perspective help us go further.',
  } as Bi,
  markers: [
    { id: 'team', label: { fr: 'Notre équipe', en: 'Our team' } },
    { id: 'clients', label: { fr: 'Nos clients', en: 'Our clients' } },
    { id: 'partners', label: { fr: 'Nos experts et partenaires', en: 'Our experts & partners' } },
    { id: 'ambition', label: { fr: 'Notre ambition', en: 'Our ambition' } },
  ] as { id: 'team' | 'clients' | 'partners' | 'ambition'; label: Bi }[],
}

/* ───────── Closing ───────── */
export const closing = {
  eyebrow: { fr: 'Près de neuf ans plus tard', en: 'Nearly nine years later' } as Bi,
  headline: [
    { text: { fr: 'Nous poursuivons toujours', en: 'We are still pursuing' } },
    { text: { fr: 'la même idée.', en: 'the same idea.' }, accent: true },
  ] as Line[],
  body: {
    fr: 'Enlever la complexité entre une personne, une organisation et ce qu’elles cherchent à accomplir.',
    en: 'Removing the complexity between a person, an organization and what they are trying to accomplish.',
  } as Bi,
  changed: [
    { fr: 'Nous avons changé.', en: 'We have changed.' },
    { fr: 'La technologie a changé.', en: 'Technology has changed.' },
  ] as Bi[],
  obsession: { fr: 'Notre obsession, elle, est restée la même.', en: 'Our obsession has stayed the same.' } as Bi,
  imageAlt: { fr: 'Une personne de dos, veste ATNA, regarde une ville au coucher du soleil.', en: 'A person seen from behind, wearing an ATNA jacket, looking at a city at sunset.' } as Bi,
  promise: [
    { text: { fr: 'Plus de capacité.', en: 'More capacity.' } },
    { text: { fr: 'Moins de complexité.', en: 'Less complexity.' }, accent: true },
  ] as Line[],
  cta: { fr: 'Voyons ce que nous pouvons simplifier ensemble', en: 'Let’s see what we can simplify together' } as Bi,
}
